import { useState } from 'react'
import { fbt } from 'fbtee'
import { CalendarDays } from 'lucide-react'
import { openSlots, slotFor } from '@shared/eventTickets'
import { Button } from '../../components/ui/button'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { UserAvatar } from '../../components/UserAvatar'
import { giveOpenTicket, respondToTicket } from '../../hooks/useEventTickets'
import type { CrewTicketAction } from '../../hooks/useCrewTicketActions'
import type { UserProfile } from '../../types'

function ticketLine(item: CrewTicketAction, who: string): string {
  if (item.kind === 'for_you') {
    return fbt(fbt.param('name', who) + ' has a ticket for you', 'Crew activity, under the event name: a friend assigned you one of their tickets and is waiting for your answer')
  }
  return item.kind === 'open'
    ? fbt(fbt.param('name', who) + ' has an open ticket', 'Crew activity, under the event name: you are looking for a ticket and a friend has one nobody claimed')
    : fbt(fbt.param('name', who) + ' is looking for a ticket', 'Crew activity, under the event name: you have an open ticket and a friend is looking for one')
}

/** A ticket waiting on the viewer in Crew's inbox, answerable without opening the event. */
export function TicketInboxRow({ item, viewerId, profile, name, onOpen }: {
  item: CrewTicketAction
  viewerId: string
  profile?: UserProfile
  name: string
  onOpen: () => void
}) {
  const [busy, setBusy] = useState(false)
  const run = async (action: () => Promise<unknown>) => {
    if (busy) return
    setBusy(true)
    try { await action() } finally { setBusy(false) }
  }
  const { ledger, mine } = item
  const mySlot = slotFor(ledger, viewerId)
  const open = openSlots(ledger)[0]

  return (
    <li className="flex items-center gap-2.5" data-testid="crew-inbox-ticket">
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left">
        <span className="relative shrink-0">
          <ThumbStack srcs={[item.event.imageUrl]} icon={CalendarDays} />
          <UserAvatar profile={profile} fallbackName={name} size="xs" className="absolute -bottom-1 -right-1 z-10 ring-2 ring-card" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{item.event.name}</span>
          <span className="block truncate text-xs text-muted-foreground">{ticketLine(item, name)}</span>
        </span>
      </button>
      {item.kind === 'for_you' && mySlot && (
        <>
          <Button size="sm" className="h-8" disabled={busy} onClick={() => run(() => respondToTicket(ledger, mySlot.id, 'accept'))}>
            {fbt('Accept', 'Crew inbox button accepting a ticket someone assigned to the viewer, which also marks them going')}
          </Button>
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => run(() => respondToTicket(ledger, mySlot.id, 'decline'))}>
            {fbt('Decline', 'Crew feed menu item turning down a ticket someone assigned to the viewer')}
          </Button>
        </>
      )}
      {item.kind === 'open' && open && (
        <Button size="sm" className="h-8" disabled={busy} onClick={() => run(() => respondToTicket(ledger, open.id, 'claim'))}>
          {fbt('I want it', 'Crew feed menu item claiming a friend’s open ticket')}
        </Button>
      )}
      {item.kind === 'seeking' && mine && (
        <Button size="sm" className="h-8" disabled={busy} onClick={() => run(() => giveOpenTicket(mine, ledger.userId))}>
          {fbt('Give my ticket', 'Crew feed menu item handing the viewer’s open ticket to a friend looking for one')}
        </Button>
      )}
    </li>
  )
}
